import type { FC } from "react"
import { useEffect, useState, useCallback } from "react"
import {
  Button,
  Center,
  Flex,
  HStack,
  Select,
  Skeleton,
  Stack,
  Switch,
  Text,
  Tooltip,
} from "@chakra-ui/react"
import { AccountId as CaipAccountId } from "caip"
import { isMobile } from "react-device-detect"

import GovernanceTreasuryContractLabel from "governance/components/GovernanceTreasuryContractLabel"
import { ROUTES } from "common/constants/routes"
import type { SourceTreasury } from "common/hooks/useTokenBalances"
import { useMultiAddressTokenBalances } from "common/hooks/useTokenBalances"
import AddressAssetsTable from "address/components/AddressAssetsTable"
import type { ChainId, Governor } from "query/graphql"
import {
  GovernorKind,
  useGovernanceTreasuryInformationQuery,
} from "query/graphql"
import { getChainIdParams } from "web3/helpers/chainId"
import AlertMessage from "common/components/AlertMessage"
import Link from "common/components/Link"
import CardContainer from "common/components/CardContainer"
import type { TokenBalance, TokenBalances } from "common/types/tokenBalances"
import StatBox from "common/components/StatBox"
import { formatDollars } from "common/helpers/formatDollars"
import { getMainnetChainIds } from "governance/helpers/governance"
import { getCustomSourceTreasuries } from "governance/helpers/treasury"
import { getWhiteLabelRoute } from "whitelabel/utils/breadcrumb"
import { addressToAccountId, parseAccountId } from "web3/helpers/transformers"

type GovernanceTreasuryInformationProps = {
  slug: string
  displayTop?: boolean
  governors: Governor[]
  isWhiteLabel: boolean
  governorIds: string[]
  organizationId: string
}
const GovernanceTreasuryInformation: FC<GovernanceTreasuryInformationProps> = ({
  slug,
  governorIds,
  governors,
  isWhiteLabel,
  organizationId,
  displayTop = true,
}) => {
  const [activeSourceTreasuries, setActiveSourceTreasuries] = useState<
    SourceTreasury[]
  >([])
  const [sourceTreasuries, setSourceTreasuries] = useState<SourceTreasury[]>([])
  const [treasuryAccountId, setTreasuryAccountId] = useState<string | null>(
    null,
  )

  const sourceGovernanceIds = governorIds

  const { data, isLoading } = useGovernanceTreasuryInformationQuery({
    input: {
      filters: {
        organizationId,
      },
    },
  })

  const {
    tokenBalances,
    isLoading: isBalanceLoading,
    isError,
  } = useMultiAddressTokenBalances(activeSourceTreasuries)

  const getTreasuryAddress = (governor: Governor) => {
    const { contracts, timelockId } = governor

    const { governor: governorContract } = contracts
    const { address: governorAddress } = governorContract

    const treasuryAddress = timelockId
      ? CaipAccountId.parse(timelockId).address
      : governorAddress

    return treasuryAddress
  }

  useEffect(() => {
    if (data && data.governors) {
      const { governors } = data

      const mainnetChainIds = getMainnetChainIds()

      const filteredSourceTreasuries = (governors.nodes as Governor[])
        .filter((governor) => sourceGovernanceIds.includes(governor.id))
        .filter((governor) =>
          [
            GovernorKind.Multiprimary,
            GovernorKind.Multisecondary,
            GovernorKind.Single,
            GovernorKind.Hub,
            GovernorKind.Spoke,
          ].includes(governor.kind),
        )
        .map((governor) => {
          const { contracts, timelockId, id } = governor
          const { tokens } = contracts
          const { chainId } = parseAccountId(id)
          const { reference } = getChainIdParams(chainId)

          const treasuryAddress = getTreasuryAddress(governor)

          const isMainnetChain = mainnetChainIds.some(
            (chainFilter) => chainFilter === chainId,
          )

          const whitelistTokenAddresses = isMainnetChain
            ? tokens.map((token) => token.address)
            : []

          return {
            accountId: addressToAccountId(treasuryAddress, reference),
            whitelistTokenAddresses,
            name: `${governor.name} ${timelockId ? "Timelock" : "Governor"}`,
          }
        })

      const customSourceTreasuries = getCustomSourceTreasuries({
        slug,
        sourceTreasuries: filteredSourceTreasuries,
      })

      setSourceTreasuries(customSourceTreasuries)
      setActiveSourceTreasuries(customSourceTreasuries)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, sourceGovernanceIds])

  useEffect(() => {
    if (activeSourceTreasuries && activeSourceTreasuries.length === 1) {
      const [sourceTreasury] = activeSourceTreasuries
      setTreasuryAccountId(sourceTreasury.accountId)
    } else {
      setTreasuryAccountId(null)
    }
  }, [activeSourceTreasuries])

  const [displayUnknownToken, setDisplayUnknownToken] = useState(false)
  const [totalBalanceLabel, setTotalBalanceLabel] = useState("")
  const [isCalculatingBalance, setIsCalculatingBalance] =
    useState<boolean>(false)

  const getTotalBalanceLabel = useCallback(async () => {
    setIsCalculatingBalance(true)

    const filterValidTokens = async (tokens: TokenBalance[]) => {
      const validTokensPromises = tokens.map(async (token) => {
        if (
          // Check if the token is a DAO token
          "contracts" in governance &&
          governance.contracts?.tokens?.some(
            (ta) =>
              ta.address.toLowerCase() ===
              token.tokenInfo.address.toLowerCase(),
          )
        )
          return token

        if (!token.tokenInfo.logo && !displayUnknownToken) return null

        const isValid = token.tokenInfo.logo
          ? await checkImageValidity(token.tokenInfo.logo)
          : false

        if (!displayUnknownToken && !isValid) return null

        return token
      })

      const resolvedTokens = await Promise.all(validTokensPromises)

      return resolvedTokens.filter((token) => token !== null)
    }

    const { nativeAsset, tokens = [] } = tokenBalances || {}

    const allTokens = [...tokens].filter(
      (token): token is TokenBalance => token !== undefined,
    )

    const validTokens = await filterValidTokens(allTokens)

    const balance = validTokens.reduce(
      (acc, token) => acc + (Number(token?.tokenInfo?.fiat) || 0),
      0,
    )

    const totalBalance = (balance || 0) + (nativeAsset?.tokenInfo?.fiat || 0)

    const { formattedDollars, formattedCents } = formatDollars(totalBalance)

    const value = `${formattedDollars}.${formattedCents} USD`

    setTotalBalanceLabel(value)
    setIsCalculatingBalance(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenBalances, displayUnknownToken])

  useEffect(() => {
    if (tokenBalances) {
      getTotalBalanceLabel()
    }
  }, [tokenBalances, displayUnknownToken, getTotalBalanceLabel])

  if (isLoading) return null

  if (data === undefined) return null

  const [governance] = data.governors.nodes

  if (!governance) return null

  const { timelockId } = governance as Governor

  const getTitle = () => {
    if (governors.length > 1) {
      return "DAO Assets"
    }

    return timelockId ? "Timelock Assets" : "Governor Assets"
  }

  const title = getTitle()

  const getTokenPriceWarning = () => {
    const hasPriceData = tokenBalances?.nativeAsset?.tokenInfo?.fiat !== null

    return isError || !hasPriceData
  }

  const showTokenPriceWarning = getTokenPriceWarning()

  return (
    <CardContainer
      isTableView
      elementTitle={
        <Flex
          align="center"
          cursor="pointer"
          justify="space-between"
          width="100%"
        >
          <Text>{title}</Text>
          <HStack>
            <Tooltip
              label={
                displayUnknownToken
                  ? `Include assets without logo.`
                  : `Ignore assets without logo.`
              }
            >
              <Text fontSize={14}>
                {displayUnknownToken
                  ? `Hide unknown${isMobile ? "" : " assets"}`
                  : `Display unknown${isMobile ? "" : " assets"}`}
              </Text>
            </Tooltip>
            <Switch
              isChecked={displayUnknownToken}
              onChange={() => {
                setDisplayUnknownToken(!displayUnknownToken)
              }}
            />
          </HStack>
        </Flex>
      }
    >
      <Stack as="section" pt={{ base: 4, sm: 0 }} spacing={4} w="full">
        <Stack
          alignItems="center"
          direction={{ base: "column", sm: "row" }}
          justifyContent="space-between"
          px={{ base: 2, sm: 6 }}
          spacing={8}
        >
          <StatBox
            isLoading={isBalanceLoading || isCalculatingBalance}
            label="Total Balance"
            size="2xl"
            value={totalBalanceLabel}
          />

          <Stack
            align="center"
            direction={{ base: "column-reverse", md: "row" }}
            width={{ base: "100%", md: "auto" }}
          >
            {treasuryAccountId ? (
              <GovernanceTreasuryContractLabel
                treasuryAddress={parseAccountId(treasuryAccountId).address}
              />
            ) : null}

            {sourceTreasuries.length > 1 ? (
              <GovernanceTreasurySelector
                sourceTreasuries={sourceTreasuries}
                treasuryAccountId={treasuryAccountId}
                onSelectActiveSourceTreasuries={(sourceTreasuries) =>
                  setActiveSourceTreasuries(sourceTreasuries)
                }
              />
            ) : null}
          </Stack>
        </Stack>
        {showTokenPriceWarning ? (
          <AlertMessage status="warning">
            Token price data is not available at this time
          </AlertMessage>
        ) : (
          <>
            <TreasuryTokenBalances
              chainId={
                treasuryAccountId
                  ? parseAccountId(treasuryAccountId).chainId
                  : undefined
              }
              displayTop={displayTop}
              displayUnknownToken={displayUnknownToken}
              governance={governance as Governor}
              isLoading={isBalanceLoading}
              tokenBalances={tokenBalances}
            />

            {displayTop ? (
              <Center>
                <Link
                  className="no-underline"
                  href={
                    isWhiteLabel
                      ? getWhiteLabelRoute(
                          ROUTES.governance.treasury.list(slug),
                        )
                      : ROUTES.governance.treasury.list(slug)
                  }
                >
                  <Button
                    data-qa="governancetreasury-viewall-btn"
                    size="sm"
                    variant="tertiary"
                  >
                    View all
                  </Button>
                </Link>
              </Center>
            ) : null}
          </>
        )}
      </Stack>
    </CardContainer>
  )
}

export default GovernanceTreasuryInformation

type GovernanceTreasurySelectorProps = {
  sourceTreasuries: SourceTreasury[]
  treasuryAccountId: string | null
  onSelectActiveSourceTreasuries: (sourceTreasuries: SourceTreasury[]) => void
}

const GovernanceTreasurySelector: FC<GovernanceTreasurySelectorProps> = ({
  sourceTreasuries,
  treasuryAccountId,
  onSelectActiveSourceTreasuries,
}) => {
  return (
    <Select
      value={treasuryAccountId ?? ""}
      width={{ base: "100%", md: "auto" }}
      onChange={(e) => {
        if (e.target.value) {
          const activeSourceTreasuries = sourceTreasuries.filter(
            (sourceTreasury: SourceTreasury) =>
              sourceTreasury.accountId === e.target.value,
          )
          onSelectActiveSourceTreasuries(activeSourceTreasuries)
        } else {
          onSelectActiveSourceTreasuries(sourceTreasuries)
        }
      }}
    >
      <option value="">All Sources</option>
      {sourceTreasuries.map((sourceTreasury) => (
        <option
          key={`treasury-${sourceTreasury.accountId}`}
          value={sourceTreasury.accountId}
        >
          {sourceTreasury.name}
        </option>
      ))}
    </Select>
  )
}

type TreasuryTokenBalancesProps = {
  tokenBalances: TokenBalances | undefined
  isLoading: boolean
  chainId?: ChainId
  displayTop: boolean
  displayUnknownToken: boolean
  governance: Governor
}

// Even if a logo is set, it might not load or be valid, this is meant to check for that
const checkImageValidity = (url: string) => {
  return new Promise((resolve) => {
    const img = new Image()
    img.src = url
    img.onload = () => resolve(true)
    img.onerror = () => resolve(false)
  })
}

const TreasuryTokenBalances: FC<TreasuryTokenBalancesProps> = ({
  chainId,
  tokenBalances,
  isLoading,
  displayTop,
  displayUnknownToken,
  governance,
}) => {
  const [validTokens, setValidTokens] = useState<TokenBalance[] | null>(null)
  const [isFiltering, setIsFiltering] = useState<boolean>(false)

  useEffect(() => {
    const filterTokens = async () => {
      if (!tokenBalances) return []

      setIsFiltering(true)

      const { nativeAsset, tokens } = tokenBalances

      if (!tokens) return [nativeAsset]

      const allTokens = [nativeAsset, ...tokens]

      const validTokensPromises = allTokens.map(async (token) => {
        if (
          // if the token is a DAO token
          "contracts" in governance &&
          governance.contracts?.tokens.some(
            (ta) =>
              ta.address.toLowerCase() ===
              token.tokenInfo.address.toLowerCase(),
          )
        )
          return token

        if (!token.tokenInfo.logo && !displayUnknownToken) return null
        const isValid = token.tokenInfo.logo
          ? await checkImageValidity(token.tokenInfo.logo)
          : false

        if (!displayUnknownToken && !isValid) return null

        return token
      })

      const resolvedTokens = await Promise.all(validTokensPromises)
      const filteredTokens = resolvedTokens.filter(
        (token): token is TokenBalance => token !== null,
      )

      const sortedTokens = filteredTokens.sort(
        (a, b) => b.tokenInfo.fiat - a.tokenInfo.fiat,
      )

      if (displayTop) {
        setValidTokens(sortedTokens.slice(0, 4))
      } else {
        setValidTokens(sortedTokens)
      }

      setIsFiltering(false)
    }

    filterTokens()
  }, [tokenBalances, displayTop, displayUnknownToken]) // eslint-disable-line react-hooks/exhaustive-deps

  if (isLoading || isFiltering) {
    return (
      <Stack
        align="center"
        as="section"
        mt={0}
        position="relative"
        pt={2}
        w="full"
      >
        <Skeleton h={36} w="full" />
      </Stack>
    )
  }

  if (!validTokens) return null

  return (
    <Stack spacing={2} w="full">
      <AddressAssetsTable chainId={chainId} tokens={validTokens} />
    </Stack>
  )
}
